'use client';
import React, { useState, useMemo } from 'react';
import GRNTable from './GRNTable';
import GRNWizard from './GRNWizard';
import GRNDetailPanel from './GRNDetailPanel';
import Icon from '@/components/ui/AppIcon';

import { toast } from 'sonner';

export type GRNStatus = 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED';

export interface GRNLineItem {
  id: string;
  itemCode: string;
  itemName: string;
  orderedQty: number;
  receivedQty: number;
  rejectedQty: number;
  uom: string;
  unitCost: number;
  totalCost: number;
  batchNumber?: string;
  expiryDate?: string;
}

export interface GRN {
  id: string;
  grnNumber: string;
  vendor: string;
  site: string;
  poReference: string;
  deliveryDate: string;
  createdDate: string;
  createdBy: string;
  status: GRNStatus;
  lineItems: GRNLineItem[];
  totalValue: number;
  inspectionNotes?: string;
  rejectionReason?: string;
  approvedBy?: string;
  approvedDate?: string;
}

export const MOCK_GRNS: GRN[] = [
  {
    id: 'grn-001',
    grnNumber: 'GRN-2026-0847',
    vendor: 'Tata Steel Ltd',
    site: 'Site Alpha',
    poReference: 'PO-2026-0312',
    deliveryDate: '28 Jul 2026',
    createdDate: '28 Jul 2026',
    createdBy: 'Rahul Kumar',
    status: 'PENDING_APPROVAL',
    totalValue: 38400,
    inspectionNotes: 'Material received in good condition. 2 bags slightly damaged — accepted.',
    lineItems: [
      { id: 'gli-001-1', itemCode: 'STL-REBAR-12', itemName: 'TMT Steel Rebar 12mm', orderedQty: 50, receivedQty: 50, rejectedQty: 0, uom: 'MT', unitCost: 680, totalCost: 34000, batchNumber: 'TATA-2026-07' },
      { id: 'gli-001-2', itemCode: 'CHEM-CEM-OPC', itemName: 'OPC Cement 53 Grade', orderedQty: 600, receivedQty: 595, rejectedQty: 5, uom: 'BAG', unitCost: 7.20, totalCost: 4284, batchNumber: 'ACC-2026-07' },
    ],
  },
  {
    id: 'grn-002',
    grnNumber: 'GRN-2026-0846',
    vendor: 'Siemens India Pvt Ltd',
    site: 'Warehouse B',
    poReference: 'PO-2026-0310',
    deliveryDate: '27 Jul 2026',
    createdDate: '27 Jul 2026',
    createdBy: 'Rahul Kumar',
    status: 'PENDING_APPROVAL',
    totalValue: 12750,
    lineItems: [
      { id: 'gli-002-1', itemCode: 'ELE-CBL-25', itemName: 'Electrical Cable 2.5mm²', orderedQty: 2000, receivedQty: 2000, rejectedQty: 0, uom: 'M', unitCost: 1.35, totalCost: 2700 },
      { id: 'gli-002-2', itemCode: 'TOOL-GRND-4IN', itemName: 'Angle Grinder 4" 850W', orderedQty: 10, receivedQty: 10, rejectedQty: 0, uom: 'EA', unitCost: 68, totalCost: 680 },
      { id: 'gli-002-3', itemCode: 'MECH-BRNG-6205', itemName: 'Deep Groove Ball Bearing 6205', orderedQty: 50, receivedQty: 50, rejectedQty: 0, uom: 'EA', unitCost: 18.40, totalCost: 920 },
    ],
  },
  {
    id: 'grn-003',
    grnNumber: 'GRN-2026-0845',
    vendor: 'Tata Steel Ltd',
    site: 'Site Beta',
    poReference: 'PO-2026-0308',
    deliveryDate: '26 Jul 2026',
    createdDate: '26 Jul 2026',
    createdBy: 'Anita Desai',
    status: 'APPROVED',
    totalValue: 28400,
    approvedBy: 'Arjun Mehta',
    approvedDate: '28 Jul 2026',
    lineItems: [
      { id: 'gli-003-1', itemCode: 'STL-REBAR-12', itemName: 'TMT Steel Rebar 12mm', orderedQty: 40, receivedQty: 40, rejectedQty: 0, uom: 'MT', unitCost: 680, totalCost: 27200 },
      { id: 'gli-003-2', itemCode: 'WELD-ROD-308', itemName: 'Welding Electrode SS 308', orderedQty: 84, receivedQty: 84, rejectedQty: 0, uom: 'KG', unitCost: 14.20, totalCost: 1192.80 },
    ],
  },
  {
    id: 'grn-004',
    grnNumber: 'GRN-2026-0844',
    vendor: 'Bosch Rexroth AG',
    site: 'Site Alpha',
    poReference: 'PO-2026-0305',
    deliveryDate: '25 Jul 2026',
    createdDate: '25 Jul 2026',
    createdBy: 'Rahul Kumar',
    status: 'PENDING_APPROVAL',
    totalValue: 67100,
    inspectionNotes: 'All items verified against delivery challan. Hydraulic components sealed.',
    lineItems: [
      { id: 'gli-004-1', itemCode: 'LUB-HYD-46', itemName: 'Hydraulic Oil ISO 46', orderedQty: 500, receivedQty: 500, rejectedQty: 0, uom: 'L', unitCost: 4.80, totalCost: 2400 },
      { id: 'gli-004-2', itemCode: 'MECH-BRNG-6205', itemName: 'Deep Groove Ball Bearing 6205', orderedQty: 100, receivedQty: 98, rejectedQty: 2, uom: 'EA', unitCost: 18.40, totalCost: 1803.20 },
    ],
  },
  {
    id: 'grn-005',
    grnNumber: 'GRN-2026-0843',
    vendor: 'Safety First Pvt Ltd',
    site: 'Site Gamma',
    poReference: 'PO-2026-0301',
    deliveryDate: '22 Jul 2026',
    createdDate: '22 Jul 2026',
    createdBy: 'Meena Pillai',
    status: 'REJECTED',
    totalValue: 4800,
    rejectionReason: 'Delivery challan does not match PO quantities. Vendor to re-submit with corrected documentation.',
    lineItems: [
      { id: 'gli-005-1', itemCode: 'SAF-HARN-01', itemName: 'Full Body Safety Harness', orderedQty: 20, receivedQty: 20, rejectedQty: 20, uom: 'EA', unitCost: 142, totalCost: 2840, batchNumber: 'SF-2026-07', expiryDate: '22 Jul 2028' },
      { id: 'gli-005-2', itemCode: 'PPE-HELM-WHT', itemName: 'Safety Helmet White (ISI)', orderedQty: 50, receivedQty: 50, rejectedQty: 50, uom: 'EA', unitCost: 12.50, totalCost: 625 },
    ],
  },
  {
    id: 'grn-006',
    grnNumber: 'GRN-2026-0842',
    vendor: 'ABB Limited',
    site: 'Warehouse B',
    poReference: 'PO-2026-0298',
    deliveryDate: '20 Jul 2026',
    createdDate: '20 Jul 2026',
    createdBy: 'Rahul Kumar',
    status: 'APPROVED',
    totalValue: 22340,
    approvedBy: 'Arjun Mehta',
    approvedDate: '22 Jul 2026',
    lineItems: [
      { id: 'gli-006-1', itemCode: 'ELE-CBL-25', itemName: 'Electrical Cable 2.5mm²', orderedQty: 5000, receivedQty: 5000, rejectedQty: 0, uom: 'M', unitCost: 1.35, totalCost: 6750 },
    ],
  },
  {
    id: 'grn-007',
    grnNumber: 'GRN-2026-0841',
    vendor: 'Castrol India Ltd',
    site: 'Warehouse B',
    poReference: 'PO-2026-0294',
    deliveryDate: '18 Jul 2026',
    createdDate: '18 Jul 2026',
    createdBy: 'Anita Desai',
    status: 'APPROVED',
    totalValue: 9600,
    approvedBy: 'Arjun Mehta',
    approvedDate: '19 Jul 2026',
    lineItems: [
      { id: 'gli-007-1', itemCode: 'LUB-HYD-46', itemName: 'Hydraulic Oil ISO 46', orderedQty: 2000, receivedQty: 2000, rejectedQty: 0, uom: 'L', unitCost: 4.80, totalCost: 9600 },
    ],
  },
  {
    id: 'grn-008',
    grnNumber: 'GRN-2026-0840',
    vendor: 'Weldex Consumables',
    site: 'Site Beta',
    poReference: 'PO-2026-0290',
    deliveryDate: '15 Jul 2026',
    createdDate: '15 Jul 2026',
    createdBy: 'Rahul Kumar',
    status: 'DRAFT',
    totalValue: 3124,
    lineItems: [
      { id: 'gli-008-1', itemCode: 'WELD-ROD-308', itemName: 'Welding Electrode SS 308', orderedQty: 220, receivedQty: 0, rejectedQty: 0, uom: 'KG', unitCost: 14.20, totalCost: 3124 },
    ],
  },
];

const STATUS_TABS = ['ALL', 'DRAFT', 'PENDING_APPROVAL', 'APPROVED', 'REJECTED'] as const;

export default function GRNContent() {
  const [statusTab, setStatusTab] = useState<typeof STATUS_TABS[number]>('ALL');
  const [search, setSearch] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [detailGrn, setDetailGrn] = useState<GRN | null>(null);

  const filtered = useMemo(() => {
    return MOCK_GRNS.filter((g) => {
      const matchTab = statusTab === 'ALL' || g.status === statusTab;
      const matchSearch = search === '' ||
        g.grnNumber.toLowerCase().includes(search.toLowerCase()) ||
        g.vendor.toLowerCase().includes(search.toLowerCase()) ||
        g.poReference.toLowerCase().includes(search.toLowerCase());
      return matchTab && matchSearch;
    });
  }, [statusTab, search]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { ALL: MOCK_GRNS.length };
    STATUS_TABS.slice(1).forEach((s) => { c[s] = MOCK_GRNS.filter((g) => g.status === s).length; });
    return c;
  }, []);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-600 text-foreground">GRN Management</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Goods Receipt Notes — 3-step workflow: Create → Inspect → Approve
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => toast.success('GRN register exported.')}
            className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg text-sm font-500 hover:bg-muted transition-colors bg-card"
          >
            <Icon name="ArrowDownTrayIcon" size={15} />
            Export Register
          </button>
          <button
            onClick={() => setWizardOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-lg text-sm font-600 hover:bg-amber-500 transition-all active:scale-95 shadow-card"
          >
            <Icon name="PlusIcon" size={16} />
            New GRN
          </button>
        </div>
      </div>

      {/* Workflow info strip */}
      <div className="flex items-center gap-4 px-4 py-3 bg-secondary/5 border border-secondary/20 rounded-xl">
        {[
          { step: 1, label: 'CREATE', desc: 'Draft entry', icon: 'DocumentPlusIcon', active: true },
          { step: 2, label: 'INSPECT', desc: 'Pending approval', icon: 'MagnifyingGlassIcon', active: false },
          { step: 3, label: 'APPROVE', desc: 'Locked & immutable', icon: 'LockClosedIcon', active: false },
        ].map((s, i) => (
          <React.Fragment key={`wf-step-${s.step}`}>
            <div className={`flex items-center gap-2 ${s.active ? 'text-secondary' : 'text-muted-foreground'}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-700 ${s.active ? 'bg-secondary text-white' : 'bg-muted text-muted-foreground'}`}>
                {s.step}
              </div>
              <div className="hidden sm:block">
                <p className="text-xs font-600">{s.label}</p>
                <p className="text-[10px]">{s.desc}</p>
              </div>
            </div>
            {i < 2 && <Icon name="ChevronRightIcon" size={14} className="text-muted-foreground shrink-0" />}
          </React.Fragment>
        ))}
        <div className="ml-auto text-xs text-muted-foreground hidden md:block">
          Reversal via RET-OUT only after approval
        </div>
      </div>

      {/* Status tabs */}
      <div className="flex gap-1 flex-wrap">
        {STATUS_TABS.map((tab) => (
          <button
            key={`grn-tab-${tab}`}
            onClick={() => setStatusTab(tab)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-500 transition-all border ${
              statusTab === tab ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-muted-foreground hover:text-foreground hover:border-muted-foreground/40'
            }`}
          >
            {tab === 'ALL' ? 'All GRNs' : tab.replace('_', ' ')}
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-600 ${
              statusTab === tab ? 'bg-white/20' : 'bg-muted text-muted-foreground'
            }`}>
              {counts[tab]}
            </span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Icon name="MagnifyingGlassIcon" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search GRN number, vendor, PO reference…"
          className="w-full pl-9 pr-4 py-2.5 border border-border rounded-lg text-sm bg-card outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring transition-all"
        />
      </div>

      {/* Table */}
      <GRNTable grns={filtered} onViewDetail={setDetailGrn} />

      {/* Wizard */}
      <GRNWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />

      {/* Detail panel */}
      {detailGrn && (
        <GRNDetailPanel grn={detailGrn} onClose={() => setDetailGrn(null)} />
      )}
    </div>
  );
}